import { Component, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { CompletionMessage } from '@app-libs/model';
import { PdfExportService } from '../../../../services/utils/pdf-export.service';
import { ChatNotificationService } from '../../../../services/chat/ui/chat-notification.service';
import { isAssistantMessage } from '../../../../utils/message-format.utils';

interface ActionButton {
  id: string;
  icon: string;
  activeIcon?: string;
  loadingIcon?: string;
  tooltip: string;
  action: () => Promise<void>;
  isLoading?: () => boolean;
}

@Component({
  selector: 'app-message-actions',
  standalone: true,
  imports: [CommonModule, MatIcon, MatButtonModule, MatTooltipModule],
  templateUrl: './message-actions.component.html',
  styleUrls: ['./message-actions.component.scss'],
})
export class MessageActionsComponent {
  @Input() message: CompletionMessage | null = null;
  @Input() filteredMessages: CompletionMessage[] = [];
  @Input() activeMessageId: string | null = null;
  @Input() copiedMessageId = '';
  @Input() exportedMessageId = '';
  @Output() copySuccess = new EventEmitter<CompletionMessage>();
  @Output() exportSuccess = new EventEmitter<CompletionMessage>();
  @Output() exportError = new EventEmitter<{ message: CompletionMessage; error: string }>();

  // Loading states
  private loadingStates = {
    export: false,
  };

  buttons: ActionButton[] = [];

  constructor(
    private clipboard: Clipboard,
    private pdfExportService: PdfExportService,
    private chatNotification: ChatNotificationService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initializeButtons();
  }

  private initializeButtons(): void {
    this.buttons = [
      {
        id: 'copy',
        icon: 'content_copy',
        activeIcon: 'done_all',
        tooltip: 'Copy All',
        action: () => this.copyMessage(),
      },
      {
        id: 'export',
        icon: 'picture_as_pdf',
        activeIcon: 'check_circle',
        loadingIcon: 'hourglass_empty',
        tooltip: 'Export as PDF',
        action: () => this.exportPdf(),
        isLoading: () => this.loadingStates.export,
      },
    ];
  }

  showActions(): boolean {
    if (!this.message || !this.message.content?.trim()) {
      return false;
    }

    if (this.isAssistantMessage(this.message)) {
      const lastMessage = this.filteredMessages[this.filteredMessages.length - 1];
      return (
        lastMessage?.message_id === this.message.message_id ||
        this.activeMessageId === this.message.message_id
      );
    }
    return false;
  }

  isAssistantMessage(message: CompletionMessage): boolean {
    return isAssistantMessage(message);
  }

  private async copyMessage(): Promise<void> {
    if (!this.message?.content) return;

    this.clipboard.copy(this.message.content);
    this.chatNotification.notifyCopySuccess();
    this.copySuccess.emit(this.message);
  }

  private async exportPdf(): Promise<void> {
    if (!this.message?.content || this.loadingStates.export) return;

    this.loadingStates.export = true;
    this.cdr.detectChanges(); // Force immediate change detection

    try {
      await this.pdfExportService.exportMessageAsPdf(this.message);
      this.chatNotification.notifyPdfExportSuccess();
      this.exportSuccess.emit(this.message);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.chatNotification.notifyPdfExportError(errorMessage);
      this.exportError.emit({
        message: this.message,
        error: errorMessage,
      });
    } finally {
      this.loadingStates.export = false;
      this.cdr.detectChanges(); // Force immediate change detection
    }
  }

  getButtonIcon(button: ActionButton): string {
    // Show loading icon if applicable
    if (button.isLoading?.() && button.loadingIcon) {
      return button.loadingIcon;
    }

    // Show active icon for recently copied messages
    if (
      button.id === 'copy' &&
      this.message?.message_id === this.copiedMessageId &&
      button.activeIcon
    ) {
      return button.activeIcon;
    }

    // Show active icon for recently exported messages
    if (
      button.id === 'export' &&
      this.message?.message_id === this.exportedMessageId &&
      button.activeIcon
    ) {
      return button.activeIcon;
    }

    return button.icon;
  }

  getButtonClass(button: ActionButton): string {
    if (button.isLoading?.()) {
      return 'loading';
    }

    if (button.id === 'copy' && this.message?.message_id === this.copiedMessageId) {
      return 'active';
    }

    if (button.id === 'export' && this.message?.message_id === this.exportedMessageId) {
      return 'active';
    }

    return '';
  }

  isButtonDisabled(button: ActionButton): boolean {
    return button.isLoading?.() || false;
  }
}
